/**
 * API configuration utility for environment-based URL routing.
 * 
 * Feature flags:
 * - ?stagingEnv=true  → Use staging API
 * 
 * Uses SAM-deployed APIs (custom domains) and Lambda Function URLs for long-running operations.
 */

// API Gateway Custom Domains
const PRODUCTION_API_URL = "https://api.precisca.ai";
const STAGING_API_URL = "https://staging-api.precisca.ai";

// Lambda Function URLs for long-running operations (15min timeout vs 29s on API Gateway)
// These are deployed via SAM with FunctionUrlConfig on ReviewService
const FUNCTION_URLS: Record<string, { production: string; staging: string }> = {
    'reviewservice': {
        // Get URLs from: aws cloudformation describe-stacks --stack-name precisca-api-staging --query "Stacks[0].Outputs[?OutputKey=='ReviewServiceFunctionUrl'].OutputValue" --output text
        production: 'https://nbvtr5t3cgeibxsiuxxq25ty5m0dhdom.lambda-url.us-west-2.on.aws/',
        staging: 'https://bm7bh7wxryv3huqtgzfnjqcoya0jqxjw.lambda-url.us-west-2.on.aws'
    }
};

// Endpoints that should use Lambda Function URLs (for long-running operations)
const FUNCTION_URL_ENDPOINTS = ['finalizereview', 'modifysection'];

/**
 * Returns whether the current environment is staging.
 */
export function isStaging(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.get('stagingEnv')?.toLowerCase() === 'true';
}

/**
 * Returns the base API URL based on the current environment.
 * - ?stagingEnv=true → staging
 * - default → production
 */
export function getApiBaseUrl(): string {
    return isStaging() ? STAGING_API_URL : PRODUCTION_API_URL;
}

/**
 * Returns the full URL for a specific service and endpoint.
 * 
 * - Uses Lambda Function URLs for long-running operations (finalizereview, modifysection)
 * - Uses API Gateway for everything else
 * 
 * @param service - Service name (authservice, intakeservice, adminservice, reviewservice, airecommendationservice)
 * @param endpoint - Endpoint path (e.g., 'login', 'getcase')
 * @returns Full URL for the API call
 */
export function getServiceUrl(service: string, endpoint: string): string {
    const staging = isStaging();

    // Check if this endpoint should use Lambda Function URL (for long-running operations)
    if (FUNCTION_URL_ENDPOINTS.includes(endpoint) && FUNCTION_URLS[service]) {
        const functionUrls = FUNCTION_URLS[service];
        const baseUrl = staging ? functionUrls.staging : functionUrls.production;
        return `${baseUrl}/${service}/${endpoint}`;
    }

    // Default: use API Gateway via custom domain
    const baseUrl = staging ? STAGING_API_URL : PRODUCTION_API_URL;
    return `${baseUrl}/${service}/${endpoint}`;
}

/**
 * Returns the base URL for a specific service (without endpoint path).
 * Use this for URLs that need query parameters appended.
 * 
 * @param service - Service name (authservice, intakeservice, adminservice, reviewservice, airecommendationservice)
 * @returns Base URL for the service
 */
export function getServiceBaseUrl(service: string): { baseUrl: string; stagingSuffix: string } {
    const staging = isStaging();
    const baseUrl = staging ? STAGING_API_URL : PRODUCTION_API_URL;

    // New stack uses unified custom domain, so no crazy suffix logic needed usually,
    // but preserving the return signautre for compatibility.
    return { baseUrl: `${baseUrl}/${service}`, stagingSuffix: '' };
}


